import { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  CheckCircle2,
  Loader2,
  ArrowRight,
  ArrowLeft,
  User,
  Scissors,
  Sparkles,
  ChefHat,
  Heart,
  Plane,
  Eye,
  PartyPopper,
  Presentation,
  ShoppingBag,
  Cannabis,
  AlertCircle
} from "lucide-react";
import { toast } from "sonner";
import { getCategoryPreset } from "@/lib/categoryConfig";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const CATEGORY_ICONS = {
  hair: Scissors, makeup: Sparkles, culinary: ChefHat, wellness: Heart,
  travel: Plane, lashes: Eye, nails: Sparkles, events: PartyPopper,
  instructors: Presentation, retail: ShoppingBag, dispensary: Cannabis,
};

// Category-specific preference fields
const CATEGORY_FIELDS = {
  hair: {
    title: "Hair Preferences",
    subtitle: "Help your stylist prepare the perfect experience",
    sections: [
      {
        label: "Hair Texture",
        key: "texture",
        type: "select-multi",
        options: ["Type 1 (Straight)", "Type 2A (Wavy)", "Type 2B (Wavy)", "Type 2C (Wavy)", "Type 3A (Curly)", "Type 3B (Curly)", "Type 3C (Curly)", "Type 4A (Coily)", "Type 4B (Coily)", "Type 4C (Coily)"],
      },
      {
        label: "Current Hair Length",
        key: "length",
        type: "select-single",
        options: ["TWA (Teeny Weeny Afro)", "Ear Length", "Chin Length", "Shoulder Length", "Armpit Length", "Bra Strap Length", "Waist Length", "Beyond Waist"],
      },
      {
        label: "Current Hair State",
        key: "state",
        type: "select-multi",
        options: ["Natural", "Relaxed", "Texlaxed", "Color Treated", "Heat Trained", "Transitioning", "Loc'd", "Protective Style"],
      },
      {
        label: "Appointment Vibe",
        key: "vibe",
        type: "select-single",
        options: ["Quick and quiet", "Let's chat", "Music and vibes", "Surprise me"],
      },
      {
        label: "Anything else your stylist should know?",
        key: "notes",
        type: "textarea",
      },
    ],
  },
  makeup: {
    title: "Makeup Preferences",
    subtitle: "Share your beauty profile with your MUA",
    sections: [
      {
        label: "Skin Type",
        key: "skin_type",
        type: "select-single",
        options: ["Oily", "Dry", "Combination", "Normal", "Sensitive"],
      },
      {
        label: "Skin Tone",
        key: "skin_tone",
        type: "select-single",
        options: ["Fair", "Light", "Medium", "Tan", "Deep", "Rich"],
      },
      {
        label: "Undertone",
        key: "undertone",
        type: "select-single",
        options: ["Warm", "Cool", "Neutral"],
      },
      {
        label: "Coverage Preference",
        key: "coverage",
        type: "select-single",
        options: ["Sheer/Natural", "Medium", "Full"],
      },
      {
        label: "Finish Preference",
        key: "finish",
        type: "select-single",
        options: ["Dewy", "Matte", "Satin"],
      },
      {
        label: "Known Allergies",
        key: "allergies",
        type: "select-multi",
        options: ["Latex", "Fragrance", "Parabens", "None"],
      },
      {
        label: "Inspiration or special requests",
        key: "notes",
        type: "textarea",
      },
    ],
  },
  culinary: {
    title: "Dining Preferences",
    subtitle: "Let your chef craft the perfect menu",
    sections: [
      {
        label: "Dietary Preferences",
        key: "dietary",
        type: "select-multi",
        options: ["Vegetarian", "Vegan", "Pescatarian", "Keto", "Paleo", "Halal", "Kosher", "Gluten-Free"],
      },
      {
        label: "Food Allergies",
        key: "allergies",
        type: "select-multi",
        options: ["Peanuts", "Tree Nuts", "Dairy", "Eggs", "Shellfish", "Soy", "Wheat", "None"],
      },
      {
        label: "Spice Tolerance",
        key: "spice",
        type: "select-single",
        options: ["No spice", "Mild", "Medium", "Hot", "Extra hot"],
      },
      {
        label: "Cuisine Interests",
        key: "cuisine",
        type: "select-multi",
        options: ["Italian", "Japanese", "Mexican", "Indian", "Thai", "French", "Southern/Soul", "Mediterranean", "Caribbean", "Korean"],
      },
      {
        label: "Additional notes for your chef",
        key: "notes",
        type: "textarea",
      },
    ],
  },
  wellness: {
    title: "Wellness Preferences",
    subtitle: "Help your provider customize your experience",
    sections: [
      {
        label: "Wellness Interests",
        key: "interests",
        type: "select-multi",
        options: ["Yoga", "Meditation", "Breathwork", "Sound Healing", "Massage", "Acupuncture", "Reiki", "Aromatherapy", "Cold Plunge", "Sauna"],
      },
      {
        label: "Fitness Level",
        key: "fitness",
        type: "select-single",
        options: ["Beginner", "Intermediate", "Advanced", "Recovery"],
      },
      {
        label: "Pressure Preference (for massage/bodywork)",
        key: "pressure",
        type: "select-single",
        options: ["Light", "Medium", "Firm", "Deep tissue"],
      },
      {
        label: "Health notes or concerns",
        key: "notes",
        type: "textarea",
      },
    ],
  },
};

// Fallback generic fields for categories without specific config
const GENERIC_FIELDS = {
  title: "Your Preferences",
  subtitle: "Share your preferences with your provider",
  sections: [
    {
      label: "What are you looking for?",
      key: "goals",
      type: "textarea",
    },
    {
      label: "Any allergies or sensitivities?",
      key: "allergies",
      type: "textarea",
    },
    {
      label: "Additional notes for your provider",
      key: "notes",
      type: "textarea",
    },
  ],
};

export default function PreferenceCollectPage() {
  const { category } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Extract query params from booking platform redirect
  const hostId = searchParams.get("host") || searchParams.get("event_id") || "";
  const source = searchParams.get("ref") || searchParams.get("source") || "direct";
  const clientName = searchParams.get("name") || "";
  const clientEmail = searchParams.get("email") || "";
  const bookingRef = searchParams.get("booking") || searchParams.get("booking_ref") || "";

  const [step, setStep] = useState(1); // 1=info, 2=preferences, 3=confirmation
  const [loading, setLoading] = useState(false);
  const [hostInfo, setHostInfo] = useState(null);
  const [formData, setFormData] = useState({
    name: clientName,
    email: clientEmail,
    phone: "",
  });
  const [preferences, setPreferences] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const preset = getCategoryPreset(category || "events");
  const Icon = CATEGORY_ICONS[category] || PartyPopper;
  const fields = CATEGORY_FIELDS[category] || GENERIC_FIELDS;

  // Fetch host info if event_id provided
  useEffect(() => {
    if (hostId) {
      fetchHostInfo();
    }
  }, [hostId]);

  const fetchHostInfo = async () => {
    try {
      const res = await fetch(`${API}/events/${hostId}`, {
        headers: { "Content-Type": "application/json" },
      });
      if (res.ok) {
        const data = await res.json();
        setHostInfo(data);
      }
    } catch {
      /* host info is optional */
    }
  };

  const handlePreferenceChange = (key, value, multi = false) => {
    setPreferences(prev => {
      if (multi) {
        const current = prev[key] || [];
        if (current.includes(value)) {
          return { ...prev, [key]: current.filter(v => v !== value) };
        }
        return { ...prev, [key]: [...current, value] };
      }
      return { ...prev, [key]: value };
    });
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.email) {
      toast.error("Please fill in your name and email");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        category: category || "general",
        source,
        booking_ref: bookingRef,
        host_event_id: hostId,
        client: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
        },
        preferences,
      };

      const res = await fetch(`${API}/preferences/collect`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        setSubmitted(true);
        setStep(3);
        toast.success("Preferences saved successfully!");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to save preferences");
      }
    } catch {
      toast.error("Connection error. Please try again.");
    }
    setLoading(false);
  };

  // Step 3: Confirmation
  if (step === 3 && submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6" data-testid="collect-confirmation">
        <Card className="max-w-md w-full">
          <CardContent className="py-12 text-center">
            <div
              className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6"
              style={{ backgroundColor: preset?.bgColor || "#f0f0f0" }}
            >
              <CheckCircle2 className="w-10 h-10" style={{ color: preset?.iconColor || "#10b981" }} />
            </div>
            <h1 className="text-2xl font-bold mb-2">Preferences Saved</h1>
            <p className="text-gray-500 text-sm mb-2">
              Your {preset?.name || category} preferences have been shared with your provider.
            </p>
            {bookingRef && (
              <p className="text-xs text-gray-400 mb-6">Booking ref: {bookingRef}</p>
            )}
            <p className="text-sm text-gray-600 mb-8 max-w-xs mx-auto">
              Your preferences are now saved to your Phygital profile and can be reused across future experiences.
            </p>
            <div className="space-y-3">
              <Button
                onClick={() => navigate("/me")}
                className="w-full bg-gray-900 hover:bg-black text-white rounded-xl h-11"
                data-testid="collect-go-dashboard"
              >
                View My Preferences
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/")}
                className="w-full rounded-xl"
              >
                Back to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="preference-collect-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-100">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center gap-4">
          {step > 1 && (
            <button onClick={() => setStep(s => s - 1)} className="text-gray-400 hover:text-black transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <div className="flex items-center gap-3 flex-1">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: preset?.bgColor || "#f0f0f0" }}
            >
              <Icon className="w-5 h-5" style={{ color: preset?.iconColor || "#666" }} />
            </div>
            <div>
              <h1 className="text-sm font-semibold text-gray-900">{fields.title}</h1>
              <p className="text-xs text-gray-400">{fields.subtitle}</p>
            </div>
          </div>
          <Badge variant="outline" className="text-xs">
            Step {step} of 2
          </Badge>
        </div>
        {/* Progress bar */}
        <div className="h-0.5 bg-gray-100">
          <div
            className="h-full transition-all duration-300"
            style={{
              width: `${step === 1 ? 50 : 100}%`,
              backgroundColor: preset?.iconColor || "#666",
            }}
          />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-8">
        {/* Host info banner */}
        {hostInfo && (
          <div className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-100 mb-6" data-testid="host-info-banner">
            {hostInfo.cover_image ? (
              <img src={hostInfo.cover_image} alt="" className="w-12 h-12 rounded-lg object-cover" />
            ) : (
              <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center">
                <User className="w-5 h-5 text-gray-400" />
              </div>
            )}
            <div>
              <p className="text-sm font-medium text-gray-900">{hostInfo.name || hostInfo.title || "Your Provider"}</p>
              <p className="text-xs text-gray-400">Collecting your preferences for a personalized experience</p>
            </div>
          </div>
        )}

        {/* Source badge */}
        {source !== "direct" && (
          <div className="flex items-center gap-2 mb-4">
            <Badge variant="outline" className="text-xs text-gray-500">
              Redirected from {source.charAt(0).toUpperCase() + source.slice(1)}
            </Badge>
            {bookingRef && (
              <Badge variant="outline" className="text-xs text-gray-400">
                Booking: {bookingRef}
              </Badge>
            )}
          </div>
        )}

        {/* Step 1: Contact Info */}
        {step === 1 && (
          <Card data-testid="collect-step-info">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <User className="w-4 h-4" />
                Your Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-xs text-gray-500">Full Name *</Label>
                <Input
                  value={formData.name}
                  onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                  placeholder="Your full name"
                  className="mt-1"
                  data-testid="collect-name-input"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-500">Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
                  placeholder="your@email.com"
                  className="mt-1"
                  data-testid="collect-email-input"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-500">Phone (optional)</Label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))}
                  placeholder="(555) 000-0000"
                  className="mt-1"
                  data-testid="collect-phone-input"
                />
              </div>

              <div className="pt-4">
                <Button
                  onClick={() => {
                    if (!formData.name || !formData.email) {
                      toast.error("Name and email are required");
                      return;
                    }
                    setStep(2);
                  }}
                  className="w-full rounded-xl h-11 bg-gray-900 hover:bg-black text-white"
                  data-testid="collect-next-btn"
                >
                  Continue to Preferences
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Category-specific preferences */}
        {step === 2 && (
          <div className="space-y-5" data-testid="collect-step-prefs">
            {fields.sections.map((section) => (
              <Card key={section.key}>
                <CardContent className="pt-5">
                  <Label className="text-sm font-medium text-gray-800 mb-3 block">{section.label}</Label>

                  {section.type === "select-multi" && (
                    <div className="flex flex-wrap gap-2">
                      {section.options.map((opt) => {
                        const selected = (preferences[section.key] || []).includes(opt);
                        return (
                          <button
                            key={opt}
                            type="button"
                            onClick={() => handlePreferenceChange(section.key, opt, true)}
                            className={`px-3 py-1.5 rounded-full text-xs border transition-all ${
                              selected
                                ? "border-gray-900 bg-gray-900 text-white"
                                : "border-gray-200 bg-white text-gray-600 hover:border-gray-400"
                            }`}
                            data-testid={`pref-${section.key}-${opt.replace(/\s+/g, "-").toLowerCase()}`}
                          >
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {section.type === "select-single" && (
                    <div className="flex flex-wrap gap-2">
                      {section.options.map((opt) => {
                        const selected = preferences[section.key] === opt;
                        return (
                          <button
                            key={opt}
                            type="button"
                            onClick={() => handlePreferenceChange(section.key, opt)}
                            className={`px-3 py-1.5 rounded-full text-xs border transition-all ${
                              selected
                                ? "border-gray-900 bg-gray-900 text-white"
                                : "border-gray-200 bg-white text-gray-600 hover:border-gray-400"
                            }`}
                            data-testid={`pref-${section.key}-${opt.replace(/\s+/g, "-").toLowerCase()}`}
                          >
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {section.type === "textarea" && (
                    <Textarea
                      value={preferences[section.key] || ""}
                      onChange={e => handlePreferenceChange(section.key, e.target.value)}
                      placeholder="Type here..."
                      className="min-h-[80px]"
                      data-testid={`pref-${section.key}-textarea`}
                    />
                  )}
                </CardContent>
              </Card>
            ))}

            {/* Submit */}
            <div className="sticky bottom-0 bg-gray-50 pt-4 pb-6">
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="w-full rounded-xl h-12 text-white"
                style={{ backgroundColor: preset?.iconColor || "#111" }}
                data-testid="collect-submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                )}
                Save My Preferences
              </Button>
              <p className="text-center text-xs text-gray-400 mt-3">
                Your preferences are saved to your Phygital profile and reusable across all experiences.
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
